import type { AnyAction } from 'redux'
import type { ReactElement } from 'react'
import type { RouterState } from 'react-router'
import { isEmpty, isEqual } from 'lodash-es'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router'

import { DocumentMeta } from '../components/DocumentMeta'
import {
  NAME_ASC,
  NAME_DESC,
  PRICE_ASC,
  PRICE_DESC,
  RELEVANCE,
  createSortings,
  getDefaultSorting,
  pagingDefaults,
} from '../utils/pageAndSort'
import type { SearchDataType } from '../store/reducers/products'
import { addLegacyProductProperties, track } from '../utils/tracking'
import { getPlain } from '../store/utils'
import { getSearchQuery } from '../components/templateComponents/FacetedSearch/FacetedSearchToolbarTop'
import { loadProductsForSearchAsync } from '../store/actions'
import { parseUrlQuery } from '../../utils/parseUrlQuery'
import { useTranslate } from '../utils/translate'
import Theme from '../components/Theme'
import usePrevious from '../utils/hooks/usePrevious'

export default function Search(): ReactElement {
  const [isToolbarTopInView, setIsToolbarTopInView] = useState(true)
  const navigate = useNavigate()
  const action = useNavigationType()
  const location = useLocation()
  const previousLocation = usePrevious(location)

  const t = useTranslate('shop', 'components.productSearchComponent')

  const [searchParams] = useSearchParams()
  const previousSearchParams = usePrevious(searchParams)

  const q = searchParams.get('q')
  const sort = searchParams.get('sort') || getDefaultSorting()
  const serializedQuery = `${q}-${sort}`

  const store = useStore<State>()
  const isBeyond = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const isBusy = useSelector<State, boolean>((state) => Boolean(state.getIn(['view', 'busy'])))
  const viewError = useSelector<State, ImmutableMap>((state) => state.getIn(['view', 'error']))
  const shop = useSelector<State, ImmutableMap>((state) => state.get('shop'))

  const selectorSearchData = useSelector<State, ImmutableMap | undefined>((state) =>
    state.getIn(['searchData', serializedQuery]),
  )
  const plainSearchData = getPlain<SearchDataType | undefined>(selectorSearchData)
  const lastSearchData = useRef<SearchDataType | null>(null)
  const dispatch: GlobalDispatch = useDispatch()

  // Ensure smooth transition: use the last truthy searchData as fallback
  const searchData: SearchDataType = useMemo(
    () =>
      plainSearchData ||
      lastSearchData.current || {
        products: [],
        totalNumberOfProducts: 0,
        query: '',
        sort: 'relevance',
      },
    [plainSearchData],
  )

  useEffect(() => {
    // A missing `sort` means the default sorting: the search page is entered as `?q=…`,
    // while `getSearchQuery` always writes `sort=relevance` back.
    const normalizedParam = (params: URLSearchParams, key: string) =>
      key === 'sort' ? (params.get(key) ?? getDefaultSorting()) : params.get(key)

    // Check for changes in the location query.
    // If something relevant has changed we are not going to append products but load them new.
    // We have to combine all query parameters from current and previews location because
    // some parameters could have been removed, like search facets.
    const didSomethingChange =
      previousSearchParams &&
      [...searchParams.keys(), ...(previousSearchParams?.keys() || [])]
        // Changes to `page` mean we do actually want to append products, so we ignore them.
        .filter((key) => key !== 'page')
        .some((key) => normalizedParam(searchParams, key) !== normalizedParam(previousSearchParams, key))

    const data: SearchDataType | undefined = getPlain<SearchDataType | undefined>(searchData)
    const prevData: SearchDataType | undefined = getPlain<SearchDataType | undefined>(
      lastSearchData.current || undefined,
    )

    if (data && !isEqual(data, prevData)) {
      track('searchResults:view', {
        products: data.products.map((product) => addLegacyProductProperties(product, isBeyond)),
        query: parseUrlQuery(location.search),
      })
    }

    const didRemoveSearchTerm = previousSearchParams?.get('q') !== '*' && searchParams.get('q') === '*'
    const didChangeFiltersOrPageForSearchTerm =
      previousSearchParams?.get('q') === searchParams.get('q') && !isEqual(previousLocation, location)

    if (didRemoveSearchTerm || didChangeFiltersOrPageForSearchTerm) {
      // Only a growing page number means "fetch the next page and add it". Searching the
      // same term again drops `page`, and appending there would duplicate what is in the store.
      const page = Number(searchParams.get('page')) || pagingDefaults.page
      const previousPage = Number(previousSearchParams?.get('page')) || pagingDefaults.page

      dispatch(
        loadProductsForSearchAsync(
          {
            ...location,
            action,
            query: parseUrlQuery(location.search),
          },
          !didSomethingChange && page > previousPage,
        ),
      )
    }

    if (data) {
      lastSearchData.current = getPlain<SearchDataType | undefined>(data) || null
    }
  }, [searchParams, previousSearchParams, searchData, location, isBeyond, previousLocation, dispatch, action])

  if (viewError) return <Theme withLayout error={viewError} currentView="page" />

  const queryPage = parseInt(searchParams.get('page') || '0')
  const sortingOptions = isBeyond
    ? { ...RELEVANCE, ...PRICE_ASC, ...PRICE_DESC }
    : { ...RELEVANCE, ...NAME_ASC, ...NAME_DESC, ...PRICE_ASC, ...PRICE_DESC }

  const sortings = createSortings(sort, sortingOptions)

  const loadedPageCount = Math.ceil(searchData.products.length / pagingDefaults.resultsPerPage)
  const pageCount = Math.ceil(searchData.totalNumberOfProducts / pagingDefaults.resultsPerPage)
  const showLoadMoreButton = searchData.products.length < searchData.totalNumberOfProducts
  const templateName = q && !isEmpty(searchData.facets) ? 'FacetedSearch' : 'Search'

  const trackProductClick = (product: Core.Product, productIndex: number) => {
    track('product:click', {
      type: 'search',
      detail: q,
      product: addLegacyProductProperties(product, isBeyond),
      productIndex,
    })
  }

  const searchTitle = q ? t('resultsCard.resultsState.title', { queryString: q }) : t('submitButton.label')

  return (
    <>
      <DocumentMeta
        title={`${searchTitle} - ${shop.get('title')}`}
        meta={[{ name: 'robots', content: 'noindex, follow' }]}
      />
      <Theme withLayout currentView="Search">
        {(renderView: any, props: any) =>
          renderView(templateName, {
            ...props,
            searchData: {
              queryString: q,
              ...searchData,
              products: searchData.products.slice(0, (queryPage || 1) * pagingDefaults.resultsPerPage),
              sortings,
              updateSorting: (sorting: string) => {
                navigate(getSearchQuery(store, location, { sort: sorting, page: 1, q: q ?? undefined }), {
                  replace: true,
                })
              },
            },
            loadedPageCount,
            loadMoreProducts: () => {
              if (loadedPageCount >= pageCount) return

              const page = Number(searchParams.get('page')) || 1
              navigate(getSearchQuery(store, location, { sort, page: page + 1, q: q ?? undefined }), {
                replace: true,
              })
            },
            showLoadMoreButton,
            isBusy,
            showScrollButton: !isToolbarTopInView,
            onScrollIntoView: setIsToolbarTopInView,
            trackProductClick,
          })
        }
      </Theme>
    </>
  )
}

Search.storeUpdate = ({ location }: RouteComponentProps, state: State): (AnyAction | GlobalAction)[] => {
  const searchParams = new URLSearchParams(location.search)
  const q = searchParams.get('q')
  const sort = searchParams.get('sort') || getDefaultSorting()
  const serializedQuery = `${q}-${sort}`
  const searchData = state.getIn(['searchData', serializedQuery])

  const isForwardNavigation = location.action === 'PUSH'
  const isBackNavigation = location.action === 'POP'
  const useEffectWillTakeCare = location.action === 'REPLACE'

  const updates: (AnyAction | GlobalAction)[] = []
  if ((isForwardNavigation || isBackNavigation || !searchData) && !useEffectWillTakeCare) {
    updates.push(loadProductsForSearchAsync(location))
  }
  return updates
}

Search.loadableSsrChunks = (_props: RouterState, state: State) => {
  const hasFacets = !isEmpty(state.getIn(['searchData', 'facets']))
  return hasFacets ? ['FacetedSearch'] : []
}

Search.contentCreationDisabled = (): boolean => true
